import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";

import Map from "@commons/Map";
import Button from "@commons/Button";
import Text from "@commons/Typography/Text";
import Heading from "@commons/Typography/Heading";
import AttachmentPreview from "@commons/Preview/AttachmentPreview";

import Field from "../components/viewRelease/Field";
import Section from "../components/viewRelease/Section";
import ReleasesStatus from "../components/ReleasesStatus";

import { useGetReleaseById } from "@services/releases/getById";
import { updateReleaseStatus } from "@services/releases/update";
import { prepareViewRelease } from "@utils/translator/releases";
import { MENU_STATUS_OPTIONS } from "@typings/entities/releases/filter";
import { AppError } from "@utils/errors/appError";
import { usePage } from "@hooks/usePage";
import InfoModal from "@commons/Modal/InfoModal";
import { usePermissions } from "@hooks/usePermissions";

function ViewRelease() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setIsLoading } = usePage();
  const [showNotificationWarning, setShowNotificationWarning] = useState(false);
  const { can } = usePermissions();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const {
    data: release,
    isLoading,
    error,
    mutate,
  } = useGetReleaseById(id ?? null);

  const processedRelease = prepareViewRelease(release);

  useEffect(() => {
    if (!isLoading && !processedRelease) {
      toast.warning("Soltura não encontrada.");
      navigate("/managing-releases");
    }
    if (error) {
      toast.error("Erro ao carregar os dados de soltura.");
      navigate("/managing-releases");
    }
  }, [isLoading, processedRelease, error, navigate]);

  const handleStatusChange = async (statusValue: string) => {
    setAnchorEl(null);

    try {
      setIsLoading(true);
      const releaseUpdated = await updateReleaseStatus({
        id: id ?? "",
        status: statusValue,
      });
      toast.success("Status atualizado com sucesso");
      mutate();

      if (releaseUpdated.notificationsOk === false) {
        setShowNotificationWarning(true);
      }
    } catch (error) {
      const message =
        error instanceof AppError
          ? error.message
          : "Erro ao atualizar status de soltura.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  if (!processedRelease) return null;

  const canUpdateRelease = can("update", "releases");

  return (
    <>
      <div className="mx-auto space-y-4">
        <section className="p-4 rounded-md shadow-md bg-white flex flex-col sm:flex-row gap-4">
          <div className="w-full h-52 sm:w-64 sm:h-64">
            <Map.Root>
              <Map.Localization
                longitude={processedRelease.longitude}
                latitude={processedRelease.latitude}
              />
            </Map.Root>
          </div>

          <div className="flex-1 flex flex-col min-w-64">
            <div className="flex justify-between items-start gap-4 mb-4">
              <Heading size="sm">Informações Gerais</Heading>
              <Button
                customVariant="muted"
                size="auto"
                onClick={() => navigate("/managing-releases")}
              >
                <Text size="sm">Voltar</Text>
              </Button>
            </div>

            <ul className="space-y-2 text-sm flex-1">
              <Field
                label="Data Planejada"
                value={processedRelease.expectedDate}
              />
              <Field
                label="Data de Execução"
                value={processedRelease.executionDate}
              />
              <Field label="Latitude" value={processedRelease.latitude} />
              <Field label="Longitude" value={processedRelease.longitude} />
              <Field
                label="Agente Designado"
                value={processedRelease.executor.username}
              />
            </ul>

            <div className="flex justify-end items-center gap-2 mt-4">
              {ReleasesStatus(processedRelease.status)}
              {canUpdateRelease && (
                <Tooltip title="Alterar status">
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <EditIcon className="w-5 h-5" />
                  </IconButton>
                </Tooltip>
              )}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                slotProps={{
                  paper: {
                    className: "rounded-lg shadow-lg mt-2 min-w-[160px]",
                  },
                }}
              >
                {MENU_STATUS_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleStatusChange(option.value)}
                    selected={processedRelease.status === option.value}
                    className={`text-sm my-1 mx-1 rounded-full justify-center min-h-8 ${option.style} text-white`}
                  >
                    <Text size="sm">{option.label}</Text>
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
        </section>

        <Section title="Indivíduo">
          <Button
            customVariant="primary"
            size="auto"
            className="absolute top-4 right-4"
            onClick={() =>
              navigate(`/individuals/${processedRelease.individual.id}`)
            }
          >
            <Text size="sm">Ver</Text>
          </Button>
          <ul className="space-y-2 text-sm">
            <Field
              label="Nome UFU"
              value={processedRelease.individual.ufuName}
            />
            <Field
              label="Espécie"
              value={processedRelease.individual.taxonomicGroup?.species}
            />
            <Field
              label="Método de Identificação"
              value={processedRelease.individual.markingType}
            />
          </ul>
        </Section>

        {processedRelease.occurrence && (
          <Section title="Ocorrência">
            <ul className="space-y-2 text-sm">
              <Field label="Tipo" value={processedRelease.occurrence?.type} />
              <Field
                label="Latitude"
                value={processedRelease.occurrence?.locationLatitude}
              />
              <Field
                label="Longitude"
                value={processedRelease.occurrence?.locationLongitude}
              />
              <Field
                label="Observações"
                value={processedRelease.occurrence?.observations}
              />
              {processedRelease.occurrence.attachments &&
                processedRelease.occurrence.attachments.length > 0 && (
                  <AttachmentPreview
                    attachments={processedRelease.occurrence.attachments}
                    size="lg"
                  />
                )}
            </ul>
          </Section>
        )}
      </div>

      <InfoModal
        open={showNotificationWarning}
        title="Aviso sobre Notificações"
        message="O status da soltura foi alterado com sucesso, mas não foi possível enviar as notificações aos responsáveis. Por favor, avise o agente designado."
        onClose={() => setShowNotificationWarning(false)}
        buttonLabel="Entendi"
      />
    </>
  );
}

export default ViewRelease;
